import React from 'react';
import { Eye, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
interface ProductOverviewProps {
  searchQuery: string;
  filterStatus: string;
}
export function ProductOverview({
  searchQuery,
  filterStatus
}: ProductOverviewProps) {
  const products = [{
    id: 1,
    name: 'Organic Granola Bars - Chocolate Chip',
    brand: 'NatureFresh',
    complianceStatus: 'Issues Found',
    riskLevel: 'high',
    lastUpdated: '2 hours ago',
    hasIssues: true,
    image: 'https://images.unsplash.com/photo-1560963689-b5682b6440f8?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 2,
    name: 'Protein Shake Mix - Vanilla',
    brand: 'FitLife',
    complianceStatus: 'Issues Found',
    riskLevel: 'medium',
    lastUpdated: '4 hours ago',
    hasIssues: true,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 3,
    name: 'Coconut Water - Original',
    brand: 'TropicalPure',
    complianceStatus: 'Compliant',
    riskLevel: 'low',
    lastUpdated: '1 day ago',
    hasIssues: false,
    image: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 4,
    name: 'Quinoa Salad Mix - Mediterranean',
    brand: 'HealthyBowl',
    complianceStatus: 'Under Review',
    riskLevel: 'low',
    lastUpdated: '3 hours ago',
    hasIssues: false,
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 5,
    name: 'Almond Butter - Smooth',
    brand: 'NutHarvest',
    complianceStatus: 'Compliant',
    riskLevel: 'low',
    lastUpdated: '2 days ago',
    hasIssues: false,
    image: 'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 6,
    name: 'Green Tea Extract - Capsules',
    brand: 'VitalSupps',
    complianceStatus: 'Under Review',
    riskLevel: 'low',
    lastUpdated: '5 hours ago',
    hasIssues: false,
    image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=60&h=60&fit=crop&crop=center'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Compliant':
        return <CheckCircle className="w-4 h-4 text-green-600" data-id="element-333" />;
      case 'Issues Found':
        return <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-334" />;
      case 'Under Review':
        return <Clock className="w-4 h-4 text-yellow-600" data-id="element-335" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant':
        return 'bg-green-100 text-green-800';
      case 'Issues Found':
        return 'bg-red-100 text-red-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRiskDot = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return 'w-3 h-3 bg-red-500 rounded-full';
      case 'medium':
        return 'w-3 h-3 bg-yellow-500 rounded-full';
      case 'low':
        return 'w-3 h-3 bg-green-500 rounded-full';
      default:
        return 'w-3 h-3 bg-gray-500 rounded-full';
    }
  };
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || filterStatus === 'issues' && product.hasIssues || filterStatus === 'compliant' && product.complianceStatus === 'Compliant' || filterStatus === 'review' && product.complianceStatus === 'Under Review';
    return matchesSearch && matchesFilter;
  });
  return <div className="bg-white rounded-lg border" data-id="element-336">
      <div className="p-6 border-b border-gray-200" data-id="element-337">
        <div className="flex items-center justify-between" data-id="element-338">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-339">
            Product Overview
          </h2>
          <span className="text-sm text-gray-600" data-id="element-340">
            {filteredProducts.length} products
          </span>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-341">
        <table className="w-full" data-id="element-342">
          <thead className="bg-gray-50" data-id="element-343">
            <tr data-id="element-344">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-345">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-346">
                Compliance Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-347">
                Risk Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-348">
                Last Updated
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-349">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-350">
            {filteredProducts.map(product => <tr key={product.id} className="hover:bg-gray-50" data-id="element-351">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-352">
                  <div className="flex items-center" data-id="element-353">
                    <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-354" />
                    <div className="ml-4" data-id="element-355">
                      <div className="text-sm font-medium text-gray-900" data-id="element-356">
                        {product.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-357">
                        {product.brand}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-358">
                  <div className="flex items-center space-x-2" data-id="element-359">
                    {getStatusIcon(product.complianceStatus)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(product.complianceStatus)}`} data-id="element-360">
                      {product.complianceStatus}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-361">
                  <div className="flex items-center space-x-2" data-id="element-362">
                    <div className={getRiskDot(product.riskLevel)} data-id="element-363"></div>
                    <span className="text-sm text-gray-900 capitalize" data-id="element-364">
                      {product.riskLevel}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-365">
                  {product.lastUpdated}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-366">
                  <button className="text-purple-600 hover:text-purple-700 inline-flex items-center space-x-1" data-id="element-367">
                    <Eye className="w-4 h-4" data-id="element-368" />
                    <span data-id="element-369">View Details</span>
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}